import { Controller } from "@hotwired/stimulus"

// Sincroniza en vivo el bloque "Destacado del mes" (`featured_broker`) de la
// plantilla `mejores` con la tabla resumen (TC-133-1.4.1.2, US-133-1.1.1).
//
// El destacado, por diseño, solo ofrece brokers que YA están en la tabla resumen
// y solo es configurable si la tabla tiene al menos un broker. El servidor calcula
// ese estado inicial (`disabled: !hay_brokers` y las opciones del `<select>`) a
// partir de la tabla PERSISTIDA — correcto para un post `mejores` ya guardado.
//
// Pero en el flujo `regular → mejores` la tabla se rellena EN CLIENTE sin guardar,
// así que el estado servidor se queda obsoleto: el destacado seguiría deshabilitado
// y sin opciones aunque el redactor acabe de añadir brokers. Este controller cierra
// ese hueco (ronda 13 de PRD-133-1, Opción A): escucha el evento `summary-table:changed`
// que emite `summary_table_controller`, y en cada cambio de la tabla viva:
//   - repuebla las opciones del `<select>` con los brokers de la tabla, en su orden,
//     conservando la opción en blanco ("Quitar destacado") y la selección actual si
//     el broker elegido sigue en la tabla;
//   - habilita/deshabilita TODOS los campos del bloque (select + tag + claim + body);
//   - intercambia el texto de ayuda ("Añade brokers…" ↔ ayuda normal).
//
// El servidor sigue siendo la fuente del estado INICIAL; este controller solo
// gestiona el estado VIVO. No añade rutas ni modelos: el `id`/`name` de cada broker
// llegan en el detalle del evento, que la tabla arma desde el preview ya renderizado.
export default class extends Controller {
  static targets = ['select', 'help', 'emptyHelp']

  connect () {
    // La opción en blanco ("Quitar destacado (sin destacado)") la pinta el servidor
    // siempre como primera opción (`include_blank`). La capturamos para conservarla
    // al repoblar el selector, sin duplicar el literal i18n en JS.
    this.blankOption = this.hasSelectTarget ? this.selectTarget.options[0]?.cloneNode(true) : null
  }

  // Reacciona a un cambio en la tabla resumen: `event.detail.brokers` es la lista
  // `[{ id, name }]` en orden de tabla.
  sync (event) {
    const brokers = event.detail?.brokers ?? []
    this.rebuildOptions(brokers)
    this.setEnabled(brokers.length > 0)
  }

  // Repuebla el `<select>` con los brokers de la tabla, conservando la opción en
  // blanco y la selección previa si el broker sigue disponible.
  rebuildOptions (brokers) {
    if (!this.hasSelectTarget) return

    const select = this.selectTarget
    const previous = select.value

    select.replaceChildren()
    if (this.blankOption) select.appendChild(this.blankOption.cloneNode(true))

    brokers.forEach(({ id, name }) => {
      const option = document.createElement('option')
      option.value = String(id)
      option.textContent = name
      select.appendChild(option)
    })

    // Preservar la selección solo si el broker elegido sigue en la tabla; si se
    // quitó, el destacado vuelve a "sin destacado" (opción en blanco).
    select.value = brokers.some(({ id }) => String(id) === previous) ? previous : ''
  }

  // Habilita o deshabilita todos los campos del bloque y sincroniza el texto de
  // ayuda. Con la tabla vacía, el destacado no es configurable.
  setEnabled (enabled) {
    this.element.querySelectorAll('select, input, textarea').forEach(field => {
      field.disabled = !enabled
    })
    if (this.hasHelpTarget) this.helpTarget.hidden = !enabled
    if (this.hasEmptyHelpTarget) this.emptyHelpTarget.hidden = enabled
  }
}
